import React, { useState, useMemo, useEffect, useRef } from 'react'
import { useThree, useFrame } from '@react-three/fiber';
import { useGLTF } from '@react-three/drei'
import { FilterClock, useCameraControls, X, Y, selectSetActiveOrientationObject, selectSetUpAndForward, selectAddParent, selectRemoveParent } from './CameraControls';
import { checkLimits } from '../../machines';
import { V2_50 } from '../../constants/machine-state/machine';
import { toolLength } from '../../machines/v2-50';

export default function Model({ 
  joints = [ 0, 0, 0, 0, 0 ],
  smoothJoints = 1,
  targetFPS = 120,
  tool = null,
  model = null,
  modelOffsets = { X: 0, Y: 0, Z:0, A: 0, B:0, C: 0 },
  children,
  ...props 
}) {
  const group = useRef()
  const { nodes, materials } = useGLTF('./gltf/v2-50.glb')
  const { scene } = useThree();

  const xRef = useRef();
  const yRef = useRef();
  const baseRef = useRef();
  const spindleRef = useRef();
  const tableRef = useRef();
  const trunnionRef = useRef();
  const partRef = useRef();

  const addParent = useCameraControls(selectAddParent);
  const setActiveOrientationObject = useCameraControls(selectSetActiveOrientationObject);
  const removeParent = useCameraControls(selectRemoveParent);
  const setUpAndForward = useCameraControls(selectSetUpAndForward);

  const jointColors = useMemo(() => joints.map((joint, jointIndex) => checkLimits(V2_50, jointIndex, joint) ? 0x000000 : 0xaa0000 ), [ joints ]);

  const [ clock ]  = useState(new FilterClock());

  useFrame(() => {
    const performFiltering = () => {
      if(xRef.current) {
        xRef.current.position.x = xRef.current.position.x*(1-smoothJoints) + smoothJoints*joints[0];
      }
      if(yRef.current) {
        yRef.current.position.y = yRef.current.position.y*(1-smoothJoints) + smoothJoints*(-joints[1]);
      }
      if(spindleRef.current) {
        spindleRef.current.position.z = spindleRef.current.position.z*(1-smoothJoints) + smoothJoints*toolLength(joints[2]);
      }

      if(trunnionRef.current) {
        trunnionRef.current.rotation.x = trunnionRef.current.rotation.x*(1-smoothJoints) + smoothJoints*Math.PI/180*joints[3];
      }

      if(tableRef.current) {
        tableRef.current.rotation.y= tableRef.current.rotation.y*(1-smoothJoints) + smoothJoints*Math.PI/180*joints[4];
      }
    }
    let deltaT = clock.getElapsedTime();
    const singleFrameTime = 1000/targetFPS;
    while(deltaT > singleFrameTime) {
      performFiltering();
      deltaT -= singleFrameTime;
      clock.offsetStart(singleFrameTime);
    }
  });

  useEffect(() => {
    const intensity = 3;
    const intensity2 = 1.25;
    materials.BaseMaterial.envMap = scene.environment;
    materials.BaseMaterial.envMapIntensity = intensity;

    materials.BackingPlateMaterial.envMap = scene.environment;
    materials.BackingPlateMaterial.envMapIntensity = intensity;

    materials.TrunnionMaterial.envMap = scene.environment;
    materials.TrunnionMaterial.envMapIntensity = intensity;

    materials.TableMaterial.envMap = scene.environment;
    materials.TableMaterial.envMapIntensity = intensity;

    materials.SpindleMaterial.envMap = scene.environment;
    materials.SpindleMaterial.envMapIntensity = intensity;

    materials.XMaterial.envMap = scene.environment;
    materials.XMaterial.envMapIntensity = intensity;

    materials.ButtonsMaterial.envMap = scene.environment;
    materials.ButtonsMaterial.envMapIntensity = intensity2;

    materials.RailsMaterial.envMap = scene.environment;
    materials.RailsMaterial.envMapIntensity = intensity2;

    materials.ToolHolderMaterial.envMap = scene.environment;
    materials.ToolHolderMaterial.envMapIntensity = intensity2;

    const base = { name: "Base", object: baseRef.current };
    const spindle = { name: "Spindle", object: spindleRef.current };
    const table = { name: "Table", object: tableRef.current };
    const part = { name: "Part", object: partRef.current };
    const trunnion = { name: "Trunnion", object: trunnionRef.current };

    addParent(base);
    setActiveOrientationObject(base)

    addParent(spindle);
    addParent(table);
    addParent(trunnion);
    addParent(part);

    setUpAndForward(Y, X);

    return () => {
      removeParent(base);
      removeParent(spindle);
      removeParent(table);
      removeParent(trunnion);
      removeParent(part);
    };
  }, [ addParent, materials, removeParent, setUpAndForward, scene, setActiveOrientationObject ]);

  return (
    <group ref={group} {...props} dispose={null}>
      <group ref={baseRef}>
        <spotLight intensity={.15} angle={Math.PI / 8} penumbra={0.15} decay={2} position={[-30, 30, 30]} rotation={[-0.81, -0.58, -0.25]} />
        <spotLight intensity={.15} angle={Math.PI / 8} penumbra={0.15} decay={2} position={[-30, 30, -30]} rotation={[-0.81, -0.58, -0.25]} />
        <spotLight intensity={0.05} angle={Math.PI / 8} penumbra={0.15} decay={2} position={[30, 30, 30]} rotation={[-0.81, -0.58, -0.25]} />
        <spotLight intensity={0.05} angle={Math.PI / 8} penumbra={0.15} decay={2} position={[30, 30, -30]} rotation={[-0.81, -0.58, -0.25]} />
        <spotLight intensity={0.02} angle={Math.PI / 8} penumbra={0.15} decay={2} position={[30, -30, -30]} rotation={[-0.81, -0.58, -0.25]} />
        <mesh geometry={nodes.Base_1.geometry} material={materials.BaseMaterial} />
        <mesh geometry={nodes.Base_2.geometry} material={materials.RailsMaterial} />
        <mesh geometry={nodes.buttons.geometry} material={materials.ButtonsMaterial} />
        <mesh geometry={nodes.cycle_start_led.geometry} material={materials['None.002']} />
        <mesh geometry={nodes.estop_led.geometry} material={materials['None.001']} />
        <group ref={xRef}>
          <mesh geometry={nodes.x_1.geometry} material={materials.XMaterial} material-emissive={jointColors[0]}/>
          <mesh geometry={nodes.x_2.geometry} material={materials.ZRailMaterial}/>
          <mesh geometry={nodes.z.geometry} material={materials.SpindleMaterial} ref={spindleRef} material-emissive={jointColors[2]}>
            <mesh geometry={nodes.tool_holder.geometry} material={materials.ToolHolderMaterial} material-emissive={jointColors[2]}/>
            { tool }
          </mesh>
        </group>
        <mesh geometry={nodes.y.geometry} material={materials.BackingPlateMaterial} material-emissive={jointColors[1]} ref={yRef}>
          <mesh geometry={nodes.a.geometry} material={materials.TrunnionMaterial} ref={trunnionRef} material-emissive={jointColors[3]}>
            <mesh geometry={nodes.b.geometry} material={materials.TableMaterial} ref={tableRef} material-emissive={jointColors[4]}>
              <group position={[ modelOffsets.X, modelOffsets.Y, modelOffsets.Z ]} rotation-x={ modelOffsets.A*Math.PI/180 } rotation-y={ modelOffsets.B*Math.PI/180 }>
                <group ref={partRef}></group>
                {model}
              </group>
              {children}
            </mesh>
          </mesh>
        </mesh>
      </group>
    </group>
  )
}
